/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useDebouncedCallback } from 'use-debounce';
import { useEffect, useMemo, useState } from 'react';
import { API } from 'modules/dashboard/constants';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAuthUser,
  selectOrganizationId,
  selectSessionId,
} from 'modules/common/auth/selectors';
import {
  selectHotelName,
  selectLastPartialBookingDate,
  selectLocalCurrency,
  selectMaxForecastDate,
  selectPaceHorizon,
  selectPartialHours,
  selectPmsSync,
  selectResetBookingDate,
  selectTargetDate,
  selectVisualFilters,
} from 'modules/dashboard/selectors';
import useAbortableRequest from 'modules/common/hooks/use-abortable-request';
import { isRequestCanceled } from 'modules/common/utils/request';
import {
  buildPaceHeaderData,
  buildPaceTimeTabularFilters,
  getPaceSegmentColumns,
  getPaceTabularGranularity,
  isCustomBookingAfterMaxConfirmationDate,
} from '../functions';
import { handleCeruleanLogging } from '../../trend-tabs/functions';
import { getBreakdownName } from '../../trend-tabular-tabs/function';
import { paceActions } from '../slice';
import { BREAKDOWN_LIST_GETTER_COLUMNS } from '../../trend-tabs/constants';
import {
  selectHotelFocusOn,
  selectIsForecast,
  selectIsPaceDetailed,
  selectPaceAlignmentToggle,
  selectPaceBookingDate,
  selectPaceBookingPaceWindow,
  selectPaceChartBreakdown,
  selectPaceCumulativeView,
  selectPaceDateComparison,
  selectPaceFocusOn,
  selectPaceLagAligned,
  selectPaceSymmetricComparisonToggle,
  selectPaceTabularData,
} from '../selectors';
import {
  PACE_SEGMENT_CUMULATIVE_TITLE,
  PACE_SEGMENT_TABULAR_TITLE,
  PACE_TABULAR_NO_DATA,
} from '../constants';

const usePaceSegmentTabular = (setIsSegmentLoaded, isDisplay) => {
  const dispatch = useDispatch();
  const abortableRequest = useAbortableRequest();
  const alignmentToggle = useSelector(selectPaceAlignmentToggle);
  const symmetricComparisonToggle = useSelector(selectPaceSymmetricComparisonToggle);
  const hotelFocusOn = useSelector(selectHotelFocusOn);
  const user = useSelector(selectAuthUser);
  const selectedHotel = useSelector(selectHotelName);
  const resetBookingDate = useSelector(selectResetBookingDate);
  const sessionId = useSelector(selectSessionId);
  const pms = useSelector(selectPmsSync);
  const visualFilters = useSelector(selectVisualFilters);
  const chartBreakdown = useSelector(selectPaceChartBreakdown);
  const isDetailed = useSelector(selectIsPaceDetailed);
  const focusOn = useSelector(selectPaceFocusOn);
  const organizationId = useSelector(selectOrganizationId);
  const partialHours = useSelector(selectPartialHours);
  const lastBookingDate = useSelector(selectLastPartialBookingDate);
  const currencyType = useSelector(selectLocalCurrency);
  const targetDate = useSelector(selectTargetDate);
  const dateComparison = useSelector(selectPaceDateComparison);
  const paceHorizon = useSelector(selectPaceHorizon);
  const isForecast = useSelector(selectIsForecast);
  const maxForecastDate = useSelector(selectMaxForecastDate);
  const paceBookingWindow = useSelector(selectPaceBookingPaceWindow);
  const paceBookingDate = useSelector(selectPaceBookingDate);
  // Determine the granularity based on the selected pace booking window and booking date.
  // Refer to the following documentation for the detailed granularity rules:
  // https://surgeglobal.atlassian.net/wiki/x/AoDbY
  const paceGranularity = getPaceTabularGranularity(
    paceBookingDate,
    paceHorizon,
    paceBookingWindow,
    visualFilters,
    lastBookingDate
  );
  // A custom booking window entirely after the max confirmation date has no data to show
  const isNoData = isCustomBookingAfterMaxConfirmationDate(
    paceBookingWindow,
    paceBookingDate,
    lastBookingDate,
    isForecast
  );
  const storedTabularData = useSelector(selectPaceTabularData);
  const isCumulative = useSelector(selectPaceCumulativeView);
  const isLagAligned = useSelector(selectPaceLagAligned);
  //
  const [interpreterAgentData, setInterpreterAgentData] = useState(null);
  // Debounce the request to avoid excessive calls
  const fetchData = useDebouncedCallback(async () => {
    // Build the filters for the API request
    const filters = buildPaceTimeTabularFilters({
      targetDate,
      alignmentToggle,
      symmetricComparisonToggle,
      currencyType,
      hotels: selectedHotel,
      pms,
      chartBreakdown,
      visualFilters,
      isDetailed,
      focusOn,
      resetBookingDate,
      granularity: paceGranularity,
      hotelFocusOn,
      partialHours,
      dateComparison,
      lastBookingDate,
      paceHorizon,
      maxForecastDate,
      paceBookingDate,
      paceBookingWindow,
    });
    //
    let start;
    try {
      setIsSegmentLoaded(false);
      // Log the request time for performance monitoring
      handleCeruleanLogging({
        message: `Pace Segment Tabular Filters: ${JSON.stringify(filters)}`,
        sessionId,
        user,
        eventType: 'Cerulean Filters',
      });
      handleCeruleanLogging({
        message: `Pace Segment Tabular Request start: ${new Date()}`,
        sessionId,
        user,
        eventType: 'Cerulean Loading',
      });
      start = performance.now();
      // fetch breakdown list
      const breakdownListData = await abortableRequest(API.GET_JS_VISUAL_DATA, {
        organizationId,
        columns: BREAKDOWN_LIST_GETTER_COLUMNS,
        filters,
      });
      //
      const breakdown = getBreakdownName(isDetailed, visualFilters, chartBreakdown);
      const breakdownList = breakdownListData?.map((item) => item?.[breakdown]);
      if (filters?.breakDownList?.conditions) {
        const isTotalInclude = filters.breakDownList.conditions.values
          .map((v) => v.replace(/\u200B/g, '').trim())
          .includes('Total');
        filters.breakDownList.conditions.values = [
          ...breakdownList,
          ...(isTotalInclude ? ['Total'] : []),
        ];
      }
      //
      dispatch(
        paceActions.getPaceTabularData({
          organizationId,
          columns: getPaceSegmentColumns(isCumulative, isLagAligned),
          filters,
        })
      );
      setInterpreterAgentData({
        columns: getPaceSegmentColumns(isCumulative, isLagAligned),
        filters,
        title: isCumulative ? PACE_SEGMENT_CUMULATIVE_TITLE : PACE_SEGMENT_TABULAR_TITLE,
      });
    } catch (error) {
      if (isRequestCanceled(error)) return;
      handleCeruleanLogging({
        message: `Error fetching Pace Segment tabular data: ${error}`,
        sessionId,
        user,
        eventType: 'Cerulean Error',
      });
    } finally {
      // request is completed, also after an error: commands waiting for the visual can continue
      setIsSegmentLoaded(true);
      // Log the time taken for the request
      const end = performance.now();
      handleCeruleanLogging({
        message: `Pace Segment Tabular Request finished: ${new Date()}`,
        sessionId,
        user,
        eventType: 'Cerulean Loaded',
      });
      handleCeruleanLogging({
        message: `Pace Segment Tabular Request time: ${((end - start) / 1000).toFixed(2)} seconds`,
        sessionId,
        user,
        eventType: 'Cerulean Request Time',
      });
    }
  }, 500);
  useEffect(() => {
    if (!isDisplay) {
      // a visual that is not displayed has nothing to load
      setIsSegmentLoaded(true);
      return;
    }
    if (isNoData) {
      // nothing to request: drop a pending request and let waiting commands continue
      fetchData.cancel();
      setIsSegmentLoaded(true);
      return;
    }
    if (!targetDate || !paceGranularity) return;
    fetchData();
  }, [
    isNoData,
    isDisplay,
    alignmentToggle,
    symmetricComparisonToggle,
    targetDate,
    currencyType,
    selectHotelName,
    pms,
    chartBreakdown,
    visualFilters,
    isDetailed,
    focusOn,
    resetBookingDate,
    paceGranularity,
    hotelFocusOn,
    partialHours,
    lastBookingDate,
    dateComparison,
    paceHorizon,
    maxForecastDate,
    paceBookingDate,
    paceBookingWindow,
    isCumulative,
    isLagAligned,
  ]);
  //
  const headerData = useMemo(
    () =>
      buildPaceHeaderData({
        hotelName: selectedHotel?.label,
        targetDate,
        alignmentToggle,
        symmetricComparisonToggle,
        currencyType,
        aligned: alignmentToggle,
        dateComparison,
        pms,
        visualFilters,
        resetBookingDate,
        partialHours,
        lastBookingDate,
        paceHorizon,
        maxForecastDate,
        paceBookingDate,
        paceBookingWindow,
        granularity: paceGranularity,
      }),
    [
      targetDate,
      paceHorizon,
      alignmentToggle,
      symmetricComparisonToggle,
      currencyType,
      pms,
      visualFilters,
      partialHours,
      dateComparison,
      resetBookingDate,
      partialHours,
      lastBookingDate,
      paceBookingDate,
      paceBookingWindow,
      paceGranularity,
      isDisplay,
      isLagAligned,
    ]
  );
  return {
    headerData,
    interpreterAgentData: isNoData ? null : interpreterAgentData,
    paceTabularData: isNoData ? PACE_TABULAR_NO_DATA : storedTabularData,
  };
};

export default usePaceSegmentTabular;
